import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { employeeApi, RequestError, type EmployeeWeeklyReport } from '../api/client';
import { formatWeekRange, showRecordingDays, visibleWeeklyReports } from '../utils/weeklyReport';

/** 最初に出す週の数（続きは「続きを読み込む」で） */
const PAGE_SIZE = 4;

/**
 * 振り返り履歴の「週ごとの振り返り」（課題 #59 段階 4）。新しい画面は増やさず、
 * 既存の振り返り履歴に週次の一覧を新しい順で加える（2026-09-29 高尾さん決定）。
 * 週次が 1 件も無いとき（週次の API が無い古い BE を含む）は、見出しごと出さない。
 */
export default function WeeklyReportHistory() {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const [items, setItems] = useState<EmployeeWeeklyReport[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (cursor?: string | null) => {
      const token = getToken();
      if (!token) return;
      if (cursor) setLoadingMore(true);
      setError(null);
      try {
        const res = await employeeApi.getWeeklyReports(token, { limit: PAGE_SIZE, cursor });
        const data = visibleWeeklyReports(res.data ?? []);
        setItems((prev) => (cursor ? prev.concat(data) : data));
        setNextCursor(res.nextCursor ?? null);
      } catch (err) {
        if (err instanceof RequestError && err.status === 404 && !err.errorCode) {
          // 週次の API が無い古い BE。週次の欄を出さない
          if (!cursor) setItems([]);
          setNextCursor(null);
        } else {
          setError(err instanceof Error ? err.message : t('dashboard.errorLoad'));
        }
      } finally {
        setLoadingMore(false);
      }
    },
    [getToken, t]
  );

  useEffect(() => {
    load();
  }, [load]);

  if (items.length === 0 && !error) return null;

  return (
    <section className="weekly-history" aria-labelledby="weekly-history-heading" data-testid="weekly-history">
      <h2 id="weekly-history-heading">{t('weeklyReport.historyTitle')}</h2>
      {items.length > 0 && (
        <ol className="weekly-history-list">
          {items.map((r) => (
            <li key={r.week_start} className="weekly-history-item" data-testid="weekly-history-item">
              <h3 className="weekly-history-range">{formatWeekRange(r.week_start, r.week_end)}</h3>
              <p className="weekly-report-comment">{r.comment}</p>
              {showRecordingDays(r.recording_days) && (
                <p className="weekly-report-days">{t('weeklyReport.recordingDays', { count: r.recording_days })}</p>
              )}
            </li>
          ))}
        </ol>
      )}
      {error && (
        <div className="report-history-error" role="alert">
          <p>{error}</p>
          <button type="button" className="dashboard-error-retry" onClick={() => load(items.length > 0 ? nextCursor : null)}>
            {t('dashboard.retry')}
          </button>
        </div>
      )}
      {nextCursor && !error && (
        <button
          type="button"
          className="report-history-load-more"
          onClick={() => load(nextCursor)}
          disabled={loadingMore}
        >
          {loadingMore ? t('common.loading') : t('history.loadMore')}
        </button>
      )}
    </section>
  );
}
